import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "cx-point-view__filters" }
const _hoisted_2 = { class: "cx-point-view__filters__date" }
const _hoisted_3 = { class: "cx-point-view__filters__status" }
const _hoisted_4 = { class: "cx-point-view__filters__main" }
const _hoisted_5 = { class: "cx-point-view__filters__main__row" }
const _hoisted_6 = { class: "cx-point-view__filters__main__row" }
const _hoisted_7 = { class: "cx-point-view__filters__metadata" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", null, null, -1)

import { ref, Ref, computed } from 'vue';
import useAnimation from '@/composables/useAnimation';
import { isBefore } from 'date-fns';
import { useStore } from 'vuex';
import { useVuex } from '@vueblocks/vue-use-vuex';
import { HoleUnitTypes } from '@/domain/hole/HoleUnitTypes.enum';
import translationService from '@/services/translation.service';
import CxTimeRangeFilter from '@/app/filterPanel/common/CxTimeRangeFilter.vue';
import CxDrillHoleStatusFilter from './components/CxDrillHoleStatusFilter.vue';
import CxReferenceModelFilter from './components/CxReferenceModelFilter.vue';
import CxUnitNameFilter from './components/CxUnitNameFilter.vue';
import CxUnitTypeFilter from '@/app/filterPanel/common/CxUnitTypeFilter.vue';
import CxMetadataFilters from './components/CxMetadataFilters.vue';
import CxFilterActionPanel from '../common/CxFilterActionPanel.vue';
import CxMenuButton from '@/components/menuComponents/CxMenuButton.vue';
import CxFilterTitle from '../common/CxFilterTitle.vue';
import CxList from '@/components/list/CxList.vue';
import CxListItem from '@/components/list/CxListItem.vue';
import CxIcon from '@/components/CxIcon.vue';
import CxLabel from '@/components/CxLabel.vue';
import { RefModel } from '@/domain/refmodels/RefModel';
import FilterDrillHoleViewModel from '@/store/filterModule/points/modules/filterDrillHoleViewModel';
import { UnitTypes } from '@/domain/units/UnitTypes.enum';
import { DrillHoleStatusFilter } from '@/app/filterPanel/asbuilts/DrillHoleStatusFilter';
import { Unit } from '@/domain/units/Unit';
import { DrillHoleMetadataFilter } from '@/app/filterPanel/asbuilts/DrillHoleMetadataFilter';
import { gettext } from '@/translations/gettext.setup';
import { UseActions, UseMutations } from '@/store/UseVuex.types';


export default /*#__PURE__*/_defineComponent({
  __name: 'CxDrillHoleFilters',
  setup(__props) {

const { $gettext } = gettext;
const store = useStore();
const { applyAnimations, isAnimationReady } = useAnimation('CxDrillHoleFilters');
const unitTypeFilter: Ref<any> = ref(null);

const filterDrillHoleStore = useVuex('filterPoints/filterDrillHole', store);
const {
  availableMetadataFilters,
  addedMetadataFilters,
  selectedDrillHoleStatuses,
  queryOngoing,
  isExportLoading,
  selectedRefModel,
  startDate,
  endDate,
  resultDrillHoleSet
} = filterDrillHoleStore.useGetters([
  'availableMetadataFilters',
  'addedMetadataFilters',
  'selectedDrillHoleStatuses',
  'queryOngoing',
  'isExportLoading',
  'selectedRefModel',
  'startDate',
  'endDate',
  'resultDrillHoleSet'
]) as {
  availableMetadataFilters: Ref<DrillHoleMetadataFilter[]>,
  addedMetadataFilters: Ref<{ fieldName: string, values: string[] | number[], comparators: string | string[], operator: string }>,
  selectedDrillHoleStatuses: Ref<DrillHoleStatusFilter[]>,
  queryOngoing: Ref<boolean>,
  isExportLoading: Ref<boolean>,
  selectedRefModel: Ref<RefModel>,
  startDate: Ref<string>, endDate: Ref<string>,
  resultDrillHoleSet: Ref<FilterDrillHoleViewModel[]>
};
const appStore = useVuex('app', store);

const { allModels, projectStartDay } =
  appStore.useGetters(['allModels', 'projectStartDay']) as {
    allModels: Ref<RefModel[]>,
    projectStartDay: Ref<string>
  };

const { changeEndDate, changeStartDate } =
  filterDrillHoleStore.useMutations(['changeEndDate', 'changeStartDate']) as UseMutations;

const datesValid = computed(() => {
  return isBefore(new Date(startDate.value), new Date(endDate.value));
});

const labels = computed(() => {
  return {
    measurementDate: $gettext('Measurement Date'),
    holeStatus: $gettext('Hole Status'),
    mainFilters: $gettext('Main Filters'),
    metadataFilters: $gettext('Metadata Filters'),
    download: $gettext('Save Point Data As...'),
    result: $gettext('Results'),
    noResult: $gettext('No results'),
  };
});

const results = computed(() => {
  let result: { id: string, title: string }[] = [];
  if (resultDrillHoleSet.value && resultDrillHoleSet.value.length) {
    result = resultDrillHoleSet.value.map(r => ({
      id: r.id,
      title: r.name
    }));
  }
  return result;
});

const { selectedUnitNames, selectedUnitTypes, availableUnitNames } = filterDrillHoleStore.useGetters([
  'selectedUnitNames',
  'selectedUnitTypes',
  'availableUnitNames',
]) as {
  selectedUnitNames: Ref<Unit[]>,
  selectedUnitTypes: Ref<UnitTypes[]>,
  availableUnitNames: Ref<Unit[]>
};

const availableUnitTypes = computed(() => {
  return Object.values(HoleUnitTypes).map((t) => {
    return {
      id: t,
      name: translationService.get(t)
    };
  });
});

const {
  removeMetadataFilter,
  changeSelectedRefModel,
  changeUnitNames,
  changeSelectedDrillHoleStatus,
  clearFilters,
  changeMetadataFilterValues,
  changeMetadataFilterComparators
} = filterDrillHoleStore.useMutations([
  'removeMetadataFilter',
  'changeSelectedRefModel',
  'changeUnitNames',
  'changeSelectedDrillHoleStatus',
  'clearFilters',
  'changeMetadataFilterValues',
  'changeMetadataFilterComparators'
]) as UseMutations;

const {
  addMetadataFilter,
  updateSelectedUnitTypes,
  clearSelectedDrillHoleStatuses,
  filterDrillHoles,
  exportPoints
} = filterDrillHoleStore.useActions([
  'addMetadataFilter',
  'updateSelectedUnitTypes',
  'clearSelectedDrillHoleStatuses',
  'filterDrillHoles',
  'exportPoints'
]) as UseActions;

const onClearFiltersClickedHandler = () => {
  clearFilters();
  unitTypeFilter.value.clearChipsInput();
};
const onSaveAsClickedHandler = () => {

};
const onApplyClickedHandler = () => {
  filterDrillHoles();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", _hoisted_1, [
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('filter2')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_2, [
          _createVNode(CxFilterTitle, {
            title: labels.value.measurementDate,
            rank: 3
          }, null, 8, ["title"]),
          _createVNode(CxTimeRangeFilter, {
            startDate: _unref(startDate),
            changeStartDate: _unref(changeStartDate),
            endDate: _unref(endDate),
            changeEndDate: _unref(changeEndDate),
            projectStartDay: _unref(projectStartDay)
          }, null, 8, ["startDate", "changeStartDate", "endDate", "changeEndDate", "projectStartDay"])
        ], 512), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"]),
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('filter3')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_3, [
          _createVNode(CxFilterTitle, {
            title: labels.value.holeStatus,
            rank: 3
          }, null, 8, ["title"]),
          _createVNode(CxDrillHoleStatusFilter, {
            selectedDrillHoleStatuses: _unref(selectedDrillHoleStatuses),
            onChangeSelectedDrillHoleStatus: _unref(changeSelectedDrillHoleStatus),
            onClearSelectedDrillHoleStatuses: _unref(clearSelectedDrillHoleStatuses)
          }, null, 8, ["selectedDrillHoleStatuses", "onChangeSelectedDrillHoleStatus", "onClearSelectedDrillHoleStatuses"])
        ], 512), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"]),
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('filter4')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_4, [
          _createVNode(CxFilterTitle, {
            title: labels.value.mainFilters,
            rank: 3
          }, null, 8, ["title"]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(CxReferenceModelFilter, {
              refmodels: _unref(allModels),
              selectedRefModel: _unref(selectedRefModel),
              changeSelectedRefModel: _unref(changeSelectedRefModel)
            }, null, 8, ["refmodels", "selectedRefModel", "changeSelectedRefModel"]),
            _createVNode(CxUnitTypeFilter, {
              availableUnitTypes: availableUnitTypes.value,
              selectedUnitTypes: _unref(selectedUnitTypes),
              changeSelectedUnitTypes: _unref(updateSelectedUnitTypes),
              ref_key: "unitTypeFilter",
              ref: unitTypeFilter
            }, null, 8, ["availableUnitTypes", "selectedUnitTypes", "changeSelectedUnitTypes"])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(CxUnitNameFilter, {
              availableUnits: _unref(availableUnitNames),
              selectedUnits: _unref(selectedUnitNames),
              updateSelection: _unref(changeUnitNames)
            }, null, 8, ["availableUnits", "selectedUnits", "updateSelection"])
          ])
        ], 512), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"]),
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('filter5')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createElementVNode("div", _hoisted_7, [
          _createVNode(CxFilterTitle, {
            title: labels.value.metadataFilters,
            rank: 3
          }, null, 8, ["title"]),
          _createVNode(CxMetadataFilters, {
            availableMetadataFilters: _unref(availableMetadataFilters),
            addedMetadataFilters: _unref(addedMetadataFilters),
            addMetadataFilter: _unref(addMetadataFilter),
            removeMetadataFilter: _unref(removeMetadataFilter),
            changeMetadataFilterValues: _unref(changeMetadataFilterValues),
            changeMetadataFilterComparators: _unref(changeMetadataFilterComparators)
          }, null, 8, ["availableMetadataFilters", "addedMetadataFilters", "addMetadataFilter", "removeMetadataFilter", "changeMetadataFilterValues", "changeMetadataFilterComparators"])
        ], 512), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"]),
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('filter6')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createVNode(CxList, {
          items: results.value,
          header: labels.value.result,
          placeholder: "No Results"
        }, {
          default: _withCtx(({ item, selected }) => [
            _createVNode(CxListItem, {
              item: item,
              selected: selected,
              disabled: true
            }, {
              selection: _withCtx(() => [
                _hoisted_8
              ]),
              "before-content": _withCtx(({}) => [
                _createVNode(CxIcon, {
                  icon: "radio_button_unchecked",
                  size: 15,
                  class: "cxu-color-faded"
                })
              ]),
              _: 2
            }, 1032, ["item", "selected"])
          ]),
          placeholder: _withCtx(() => [
            _createVNode(CxLabel, {
              label: labels.value.noResult,
              class: "cx-point-view__filters__result-list__placeholder"
            }, null, 8, ["label"])
          ]),
          _: 1
        }, 8, ["items", "header"]), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"]),
    _createVNode(_Transition, {
      name: _unref(applyAnimations)('action')
    }, {
      default: _withCtx(() => [
        _withDirectives(_createVNode(CxFilterActionPanel, {
          onClearFiltersClicked: onClearFiltersClickedHandler,
          onApplyClicked: onApplyClickedHandler,
          onSaveAsClicked: onSaveAsClickedHandler,
          isDisabled: !datesValid.value,
          queryOngoing: _unref(queryOngoing)
        }, {
          default: _withCtx(() => [
            _createVNode(CxMenuButton, {
              text: labels.value.download,
              items: [{name: 'csv'}, {name: 'json'}, {name: 'geo'}],
              loading: _unref(isExportLoading),
              disabled: !_unref(resultDrillHoleSet).length,
              menuPosition: "top",
              onClick: _cache[0] || (_cache[0] = (selectedExtension) => _unref(exportPoints)(selectedExtension.name))
            }, null, 8, ["text", "loading", "disabled"])
          ]),
          _: 1
        }, 8, ["isDisabled", "queryOngoing"]), [
          [_vShow, _unref(isAnimationReady)]
        ])
      ]),
      _: 1
    }, 8, ["name"])
  ]))
}
}

})